import React, { useEffect, useState, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getThemeApi } from "../redux/api/api";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5055/api";

const applyThemeToDocument = (theme) => {
    if (!theme) return;
    const root = document.documentElement;
    // colors
    const colors = theme.colors || {};
    if (colors.primary) root.style.setProperty("--primary-color", colors.primary);
    if (colors.secondary) root.style.setProperty("--secondary-color", colors.secondary);
    if (colors.accent) root.style.setProperty("--accent-color", colors.accent);
    if (theme.fontFamily) root.style.setProperty("--theme-font-family", theme.fontFamily);
    if (theme.buttonColor) root.style.setProperty("--button-color", theme.buttonColor);

    // Legacy/compat variables used elsewhere in the app
    if (colors.primary) root.style.setProperty("--manu-color", colors.primary);
    if (colors.secondary) root.style.setProperty("--manuhover_color", colors.secondary);
    if (colors.accent) root.style.setProperty("--manu_text_color", colors.accent);
    if (colors.accent) root.style.setProperty("--manu_text_hover_color", colors.accent);
    if (theme.buttonColor) root.style.setProperty("--button_color", theme.buttonColor);
    if (theme.buttonTextColor) root.style.setProperty("--button_text_color", theme.buttonTextColor || "#ffffff");

    // Section/background helper variables for Website pages
    // default fallbacks will be used by components if theme lacks values
    const sectionBg = colors.secondary || "#EAF0F5";
    const sectionAltBg = colors.primary || "#DFE7ED";
    const footerBg = colors.primary ? colors.primary : "#0D263C";

    root.style.setProperty("--section-bg", sectionBg);
    root.style.setProperty("--section-alt-bg", sectionAltBg);
    root.style.setProperty("--footer-bg", footerBg);
    // layout variant
    if (theme.layoutVariant) root.setAttribute("data-layout-variant", theme.layoutVariant);
    // banner
    if (theme.bannerImage) root.style.setProperty("--banner-image", `url(${theme.bannerImage})`);
};

const getEventIdFromPath = (pathname) => {
    if (!pathname) return null;
    if (pathname.startsWith("/event/")) return pathname.replace("/event/", "").split("/")[0];
    if (pathname.startsWith("/e/")) return pathname.replace("/e/", "").split("/")[0];
    return null;
};

export const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

export default function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(null);
    const location = useLocation();

    useEffect(() => {
        let mounted = true;
        const pathname = location?.pathname || window.location.pathname;
        const eventId = getEventIdFromPath(pathname);

        // If not an event page, clear theme and do nothing
        if (!eventId) {
            // optional: remove previously applied event theme variables
            setTheme(null);
            return;
        }

        const fetchTheme = async () => {
            const response = await getThemeApi()
            if (response.data.status === 200) {
                const data = response.data.info
                const root = document.documentElement
                root.style.setProperty('--manu-color', data.manu_color)
                root.style.setProperty('--manuhover_color', data.manuhover_color)
                root.style.setProperty('--manu_text_color', data.manu_text_color)
                root.style.setProperty('--manu_text_hover_color', data.manu_text_hover_color)
                root.style.setProperty('--button_color', data.button_color)
                root.style.setProperty('--button_text_color', data.button_text_color)
            } else {
                console.error('Error fetching theme')
            }
        }
        // const fetchTheme = async () => {
        //   try {
        //     const resp = await axios.get(`${API_BASE}/event/${eventId}`);
        //     console.log("Fetched event data for theme:", resp.data);
        //     const event = resp.data?.event || resp.data?.info || resp.data;
        //     const websiteTheme = event?.website?.theme || null;
        //     if (mounted && websiteTheme) {
        //       setTheme(websiteTheme);
        //       applyThemeToDocument(websiteTheme);
        //     }
        //   } catch (err) {
        //     // ignore
        //   }
        // };

        fetchTheme();

        let socketClient = null;
        let pollingId = null;

        // Try realtime via socket.io-client, fall back to polling
        (async () => {
            try {
                const socketModule = await import("socket.io-client");
                const ioFn = socketModule?.default || socketModule?.io || socketModule;
                socketClient = ioFn(process.env.REACT_APP_SOCKET_URL || "http://localhost:5055");
                socketClient.emit("joinEvent", eventId);
                socketClient.on("themeUpdated", (data) => {
                    if (data?.theme) {
                        setTheme(data.theme);
                        applyThemeToDocument(data.theme);
                    }
                });
            } catch (e) {
                // socket client not available, use polling
                pollingId = setInterval(fetchTheme, 15000);
            }
        })();

        return () => {
            mounted = false;
            if (socketClient) {
                try {
                    socketClient.emit("leaveEvent", eventId);
                    socketClient.disconnect();
                } catch (e) { }
            }
            if (pollingId) clearInterval(pollingId);
        };
    }, [location?.pathname]);

    return (
        <ThemeContext.Provider value={{ theme }}>
            {children}
        </ThemeContext.Provider>
    );
}
